/**
 * Login orchestration: turns a raw (already-normalized) documento + password
 * into a `LoginResult` and an error-catalogue key, by composing the pure BFF
 * call (`api/bffAuth.ts`) with installing the returned tokens into the
 * Supabase client.
 *
 * Deliberately still free of React: every effectful dependency (fetch,
 * setSession) is injected, so the whole flow — including all four ways
 * login can fail — is testable without a network, a component tree, or a
 * router. See `loginFlow.test.ts`.
 */
import { requestLogin, type FetchLike, type LoginTokens } from '../api/bffAuth.ts';

export type LoginResult =
  | { ok: true }
  | { ok: false; error: 'invalid_credentials' | 'rate_limited' | 'unknown' };

/**
 * ⚠️ ONE key for every credential failure — do not add a second.
 *
 * "no such documento", "wrong password" and "expired activation credential"
 * must be indistinguishable to the client (ADR-0003). The BFF already
 * collapses them into a single uniform 401; if the browser rendered three
 * different sentences it would hand the oracle straight back. The same
 * uniformity extends to a transport failure, a malformed token payload, and
 * `setSession` rejecting the installed tokens — all FOUR failure paths below
 * resolve to this one key. Reviewers: reject any diff that introduces a
 * sibling of this constant.
 */
export const INVALID_CREDENTIALS_KEY = 'error.invalidCredentials';

export interface LoginFlowDependencies {
  fetchImpl: FetchLike;
  bffBase: string;
  /** Installs GoTrue tokens into the Supabase client. Usually `supabase.auth.setSession`. */
  setSession: (tokens: LoginTokens) => Promise<{ error: { message: string } | null }>;
}

export interface LoginFlowOutcome {
  result: LoginResult;
  /** A catalogue key, or null to clear any previous error. Never rendered text. */
  errorKey: string | null;
}

export async function performLogin(
  documento: string,
  password: string,
  deps: LoginFlowDependencies,
): Promise<LoginFlowOutcome> {
  const outcome = await requestLogin(deps.fetchImpl, deps.bffBase, documento, password);

  switch (outcome.kind) {
    case 'transport_error':
      // The request never reached the BFF — offline, or the edge is down.
      return { result: { ok: false, error: 'unknown' }, errorKey: INVALID_CREDENTIALS_KEY };

    case 'rate_limited':
      // A throttle, not a credential oracle — reported distinctly on purpose.
      return { result: { ok: false, error: 'rate_limited' }, errorKey: 'error.rateLimited' };

    case 'rejected':
      return { result: { ok: false, error: 'invalid_credentials' }, errorKey: INVALID_CREDENTIALS_KEY };

    case 'malformed':
      return { result: { ok: false, error: 'unknown' }, errorKey: INVALID_CREDENTIALS_KEY };

    case 'ok': {
      // Token REFRESH is untouched and still goes straight to GoTrue: it
      // needs a valid refresh token and is not brute-forceable, so it does
      // not belong behind the throttle. That is why the tokens are installed
      // with setSession here — supabase-js then owns persistence,
      // auto-refresh and onAuthStateChange exactly as before.
      const { error: setSessionError } = await deps.setSession(outcome.tokens);
      if (setSessionError) {
        return { result: { ok: false, error: 'unknown' }, errorKey: INVALID_CREDENTIALS_KEY };
      }

      // Session installed. The caller's onAuthStateChange listener updates
      // `session` and `portalUser`; the caller navigates on activation state.
      //
      // If the account has no portal_users row, the session exists but the
      // account was incorrectly provisioned — let the next fetch surface it.
      return { result: { ok: true }, errorKey: null };
    }
  }
}
